// The "which products" choice of an Employee Score input that comes from
// Sales & Targets, and the standard two-input set-up built on it. Shared by every
// place that asks the question (the Manage metric editor: new parameter, existing
// parameter, one input; and the Manage tab's first set-up), so they cannot drift.
import { useEffect, useId, useRef, type ReactElement } from 'react';
import { salesTargetsAPI, type SalesFeedFigure } from '../services/api';

/** One product of the project's Sales & Targets. `key` = `${productId}::`. */
export interface SalesProductOption { key: string; label: string }

/**
 * The project's Sales & Targets products, one option each.
 *
 * Always asked BY PRODUCT, whatever the project's own target granularity: a key
 * with an empty version part ("<productId>::") counts every version of that
 * product, so a version added to Product Master next month is counted without
 * anyone reopening this screen. A list of versions would silently leave it out.
 *
 * A failed read answers [] rather than throwing: the caller shows the choice as
 * unavailable, and the rest of its editor still works.
 */
export const loadSalesProductOptions = async (projectId: number): Promise<SalesProductOption[]> => {
  if (!projectId) return [];
  try {
    const res = await salesTargetsAPI.listCoordinates(projectId, 'product');
    const rows: unknown = res.data?.data;
    if (!Array.isArray(rows)) return [];
    const seen = new Set<string>();
    const out: SalesProductOption[] = [];
    for (const row of rows as Array<{ coord?: { productId?: unknown }; label?: unknown } | null>) {
      const productId = String(row?.coord?.productId ?? '').trim();
      if (!productId) continue;
      const key = `${productId}::`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ key, label: String(row?.label ?? '').trim() });
    }
    return out;
  } catch {
    return [];
  }
};

/** Product keys as the server stores them: no duplicates, sorted. */
const tidyKeys = (keys: string[]): string[] => [...new Set(keys)].sort();

/**
 * Whether two product choices count the same products. null = all products, and
 * is only equal to null; lists are compared by content, so the order a person
 * ticked them in never reads as a change.
 */
export const sameSalesProducts = (a: string[] | null | undefined, b: string[] | null | undefined): boolean => {
  if (a == null || b == null) return a == null && b == null;
  const left = tidyKeys(a);
  const right = tidyKeys(b);
  return left.length === right.length && left.every((key, i) => key === right[i]);
};

/**
 * "Choose products" with nothing ticked. It is not a way to say "all products"
 * (that is null), and the server refuses an empty list, so every caller keeps
 * its action disabled while this is true.
 */
export const noSalesProductsPicked = (value: string[] | null): boolean => value !== null && value.length === 0;

/**
 * The standard set-up: a Target input and a Sold input over the same products,
 * and the formula that divides one by the other. The formula is spaced the way
 * the Manage metric formula builder writes it, so opening the saved calculation
 * there does not read as an edit.
 */
export const salesFeedInputs = (products: string[] | null): {
  formula: string;
  sub_metrics: Array<{
    id: null;
    name: string;
    code: string;
    source_type: 'sales_targets';
    source_sales_figure: SalesFeedFigure;
    source_sales_products: string[] | null;
  }>;
} => {
  const chosen = products === null ? null : tidyKeys(products);
  return {
    formula: '( sold / target ) * 100',
    sub_metrics: [
      { id: null, name: 'Target', code: 'target', source_type: 'sales_targets', source_sales_figure: 'target', source_sales_products: chosen },
      { id: null, name: 'Sold', code: 'sold', source_type: 'sales_targets', source_sales_figure: 'sales', source_sales_products: chosen },
    ],
  };
};

interface Props {
  options: SalesProductOption[];
  /** null = all products; a list = only those (an empty list = nothing ticked yet). */
  value: string[] | null;
  onChange: (v: string[] | null) => void;
  disabled?: boolean;
}

export function SalesTargetsProductsField({ options, value, onChange, disabled }: Props): ReactElement {
  // Radios group by name across the whole page, and two of these fields can be
  // open at once (the set-up card and an input's editor).
  const groupName = useId();
  // The last list that was ticked, so a look at "All products" and back does not
  // start the ticking again from nothing.
  const lastChosen = useRef<string[]>(value ?? []);
  useEffect(() => {
    if (value !== null) lastChosen.current = value;
  }, [value]);

  const choosing = value !== null;
  const ticked = new Set(value ?? []);
  const listed = new Set(options.map((o) => o.key));
  // A saved product the project no longer lists (retired or removed in Product
  // Master). Kept on screen so it can be unticked; nothing here can add one.
  const gone = (value ?? []).filter((key) => !listed.has(key));

  const toggle = (key: string) => {
    const next = new Set(ticked);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange(tidyKeys([...next]));
  };

  const card = (checked: boolean, off: boolean) =>
    `flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium text-gray-800 transition ${
      checked ? 'border-primary-300 bg-primary-50/40' : 'border-gray-200 bg-white hover:bg-gray-50'
    } ${off ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`;
  // With nothing to tick the second card could only ever block the caller.
  const cannotChoose = !!disabled || (options.length === 0 && gone.length === 0);

  return (
    <div role="radiogroup" aria-label="Products" className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <label className={card(!choosing, !!disabled)}>
          <input
            type="radio"
            name={groupName}
            className="accent-primary-600"
            checked={!choosing}
            disabled={disabled}
            onChange={() => onChange(null)}
          />
          All products
        </label>
        <label className={card(choosing, cannotChoose)}>
          <input
            type="radio"
            name={groupName}
            className="accent-primary-600"
            checked={choosing}
            disabled={cannotChoose}
            onChange={() => onChange([...lastChosen.current])}
          />
          Choose products
        </label>
      </div>
      {choosing && (
        <ul className="max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white py-1">
          {options.map((o) => (
            <li key={o.key}>
              <label className={`flex items-center gap-2 px-2.5 py-1 text-xs text-gray-700 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-50'}`}>
                <input
                  type="checkbox"
                  className="accent-primary-600"
                  checked={ticked.has(o.key)}
                  disabled={disabled}
                  onChange={() => toggle(o.key)}
                />
                <span className="min-w-0 truncate">{o.label}</span>
              </label>
            </li>
          ))}
          {gone.map((key) => (
            <li key={key}>
              <label className={`flex items-center gap-2 px-2.5 py-1 text-xs text-amber-700 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-amber-50'}`}>
                <input
                  type="checkbox"
                  className="accent-primary-600"
                  checked
                  disabled={disabled}
                  onChange={() => toggle(key)}
                />
                No longer in Sales &amp; Targets
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
